import axios, { AxiosRequestConfig, Method } from 'axios';

import { ACCESS_TOKEN_KEY, API_SERVER } from '../contants';
import { hadSessionToken, signalSessionExpired } from '../utils/session';

const http = axios.create({ baseURL: API_SERVER });

const request = (method: Method, url: string, options: AxiosRequestConfig & { signal?: AbortSignal }) => {
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

  return http
    .request({
      ...options,
      method,
      url,
      signal: options.signal, // Pass signal to axios
      headers: {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
      },
    })
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      // Handle cancellation
      if (err.code === 'ERR_CANCELED' || err.message?.includes('canceled')) {
        // Rethrow with a special property
        throw { canceled: true };
      }
      const response = err?.response;
      if (response?.status === 401) {
        // With a token sent, a 401 means the login ran out (100 h) — tell the user once, globally
        if (hadSessionToken(accessToken)) signalSessionExpired();
        // eslint-disable-next-line no-throw-literal
        throw {
          msg: 'Unauthorized',
        };
      }
      const data = response?.data;
      // eslint-disable-next-line no-throw-literal
      throw {
        msg: data?.msg || data?.error || 'Network Error!',
        status: response?.status,
      };
    });
};

const Http = {
  async publicGet(url: string, config: any = {}) {
    return await http.get(url, config);
  },
  get(url: string, params: any = {}, headers: any = {}, signal?: AbortSignal) {
    return request('GET', url, { params, headers, signal });
  },
  // Fetch binary responses (e.g., images, PDFs) with auth
  getBlob(url: string, params: any = {}, headers: any = {}, signal?: AbortSignal) {
    return request('GET', url, { params, headers, signal, responseType: 'blob' as any });
  },
  post(url: string, body: any = {}, headers: any = {}, signal?: AbortSignal) {
    return request('POST', url, { data: body, headers, signal });
  },
  put(url: string, body: any = {}, headers: any = {}, signal?: AbortSignal) {
    return request('PUT', url, { data: body, headers, signal });
  },
  patch(url: string, body: any = {}, headers: any = {}, signal?: AbortSignal) {
    return request('PATCH', url, { data: body, headers, signal });
  },
  delete(url: string, body: any = {}, headers: any = {}, signal?: AbortSignal) {
    return request('DELETE', url, { data: body, headers, signal });
  },
};

export default Http;
